// material-ui
import { Box, Collapse, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import GradientFadeBox from 'components/@extended/GradientFadeBox';

// third-party
import { Column, useFilters, useSortBy, useTable } from 'react-table';
import { HeaderSort, TablePagination } from '../third-party/custom/ReactTableCustom';
import { EmptyTable } from '../EmptyTable';
import { CSVExport, HidingSelect } from '../third-party/ReactTable';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import React, { useEffect, useState } from 'react';

// ==============================|| REACT TABLE COMPONENT ||============================== //

interface ReactTableProps {
  columns: Column[];
  data: any[];
  dataCount: number;
  handlePageSize: (size: number) => void;
  // Only steps next/prev. TablePagination's gotoPage is wider (accepts a numeric page
  // index for the numbered buttons shown when !cursorBased); the call sites cast to bridge
  // that. Numeric jumps are effectively no-ops with this handler — pre-existing behavior.
  handlePageChange: (direction: 'next' | 'prev') => void;
  pageSize: number;
  pageIndex: number;
  getHeaderProps?: (column: any) => any;
  top?: boolean;
  isLoading?: boolean;
  isHideColumn?: boolean;
  visibleColumns?: string[];
  returnVisibleItems?: (items: any[]) => void;
  hidePaginationOnLessData?: boolean;
  hidePagePerRow?: boolean;
  csvFileName?: string;
  hidePagination?: boolean;
  border?: boolean;
  expandedRow?: number | number[] | null;
  onToggleExpand?: (rowIndex: number) => void;
  renderExpandedRow?: (rowData: any) => React.ReactNode;
  /** An always-visible summary under a collapsed row; the expanded content replaces it. */
  renderRowPreview?: (rowData: any) => React.ReactNode;
  lastTHWidth?: string;
  footer?: any;
  footerSx?: React.CSSProperties;
  fullFooterCellSx?: React.CSSProperties;
  footerCellSxByColumn?: Record<string, React.CSSProperties>;
  expandedRowPadding?: number | string;
  scrollableBody?: boolean;
  maxBodyHeight?: string | number;
  cursorBased?: boolean;
  /** Cursor mode: the server returned a cursor for a following page. Enables Next. */
  hasNextPage?: boolean;
  onGotoFirst?: () => void;
}

const ReactTable = ({
  columns,
  data,
  dataCount,
  handlePageSize,
  handlePageChange,
  pageSize = 10,
  pageIndex = 0,
  getHeaderProps,
  top,
  isLoading = true,
  isHideColumn = false,
  visibleColumns,
  returnVisibleItems,
  hidePaginationOnLessData = false,
  hidePagePerRow = false,
  csvFileName = 'table-download.csv',
  hidePagination = false,
  border = true,
  expandedRow,
  onToggleExpand,
  renderExpandedRow,
  renderRowPreview,
  lastTHWidth,
  footer,
  footerSx,
  fullFooterCellSx,
  footerCellSxByColumn,
  expandedRowPadding = 3,
  scrollableBody = false,
  maxBodyHeight = 400,
  cursorBased = false,
  hasNextPage,
  onGotoFirst
}: ReactTableProps) => {
  const theme = useTheme();
  // Next stays disabled while a page loads: the following page is only addressable once this reply
  // hands back its cursor. `undefined` passes through untouched so callers that never opted into
  // cursor paging keep the count-based rule.
  const nextEnabled = hasNextPage === undefined ? undefined : hasNextPage && !isLoading;
  const [childRowHovered, setChildRowHovered] = useState(false);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // @ts-ignore
    setPageSize,
    setHiddenColumns,
    allColumns,
    state: { hiddenColumns }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        // @ts-ignore
        pageIndex: 0,
        pageSize,
        hiddenColumns: columns
          .filter((col: Column<{}>) => visibleColumns?.includes(col.accessor as string))
          .map((col) => col.accessor) as string[]
      },
      disableSortBy: true
    },
    useFilters,
    useSortBy
  );

  // Synchronize hidden columns when visibleColumns prop changes
  useEffect(() => {
    if (visibleColumns) {
      const columnsToHide = columns
        .filter((col: Column<{}>) => visibleColumns?.includes(col.accessor as string))
        .map((col) => col.accessor) as string[];
      setHiddenColumns(columnsToHide);
    }
  }, [visibleColumns, columns, setHiddenColumns]);

  // ============================== CSV Header ============================== //
  const headers: LabelKeyObject[] = allColumns
    .filter((col) => !hiddenColumns?.includes(col.id))
    .map((col) => ({ label: col.Header as string, key: col.id }));

  const shouldShowPagination = !hidePagination && !top && !(hidePaginationOnLessData && dataCount <= pageSize);

  // ============================== Styles ============================== //
  const tableHeaderStyles = {
    paddingX: '12px',
    paddingY: 0,
    fontSize: textSizeByBreakpoint(14),
    fontWeight: 600,
    color: '#6B7580',
    lineHeight: '20px',
    border: 'none !important',
    backgroundColor: 'transparent !important',
    textTransform: 'capitalize',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  };

  const tableCellStyles = {
    paddingX: '12px',
    paddingY: sizeByBreakpoint(1, 12),
    fontSize: textSizeByBreakpoint(14),
    fontWeight: 600,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: border ? `${theme.palette.border.main} !important` : 'transparent !important',
    whiteSpace: 'nowrap'
  };

  // Supplying renderRowPreview opts a table into the attached-card footer. Without it the footer
  // keeps exactly the styling it had before previews existed, so the other tables on this shared
  // component are untouched.
  const footerCellSx = (attached: boolean, expanded: boolean) =>
    renderRowPreview
      ? {
          // !important because the theme pads every TableCell, and a plain `p: 0` only ties on
          // specificity — the leftover 12px showed as a gap between the strip and the card edge.
          padding: '0 !important',
          // Continues the row's card rather than opening a second one: sides and bottom, no top, so
          // the row and what hangs off it read as one block.
          ...(attached
            ? {
                borderTop: 'none !important',
                borderLeft: '1px solid',
                borderRight: '1px solid',
                borderBottom: '1px solid',
                borderColor: border ? `${theme.palette.border.main} !important` : 'transparent !important'
              }
            : { border: 'none !important' })
        }
      : { p: 0, border: expanded ? undefined : 'none !important' };

  // ============================== Render ============================== //
  return (
    <Stack sx={{ minHeight: 0, overflow: 'hidden' }}>
      {/* Top Pagination */}
      {top && !hidePagination && (
        <Box sx={{ p: 2 }}>
          <TablePagination
            handlePageSize={handlePageSize}
            gotoPage={handlePageChange as (value: 'next' | 'prev' | number) => void}
            rows={rows}
            dataCount={dataCount}
            setPageSize={setPageSize}
            pageIndex={pageIndex}
            pageSize={pageSize}
            cursorBased={cursorBased}
            hasNextPage={nextEnabled}
            onGotoFirst={onGotoFirst}
          />
        </Box>
      )}

      {/* Column Visibility + CSV Export */}
      {isHideColumn && (
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ p: 2 }}>
          <HidingSelect
            hiddenColumns={hiddenColumns!}
            setHiddenColumns={setHiddenColumns}
            allColumns={allColumns}
            returnVisibleItems={returnVisibleItems}
          />
          <CSVExport data={data} filename={csvFileName} headers={headers} />
        </Stack>
      )}

      {/* Table */}
      <GradientFadeBox
        fadeColor={theme.palette.background.paper}
        fadeSize={60}
        overflowX="auto"
        overflowY={scrollableBody ? 'auto' : 'hidden'}
        sx={{
          flex: '1 1 0%',
          minHeight: 0,
          ...(scrollableBody && { maxHeight: maxBodyHeight })
        }}
      >
        <Table {...getTableProps()} sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
          {/* Table Head */}
          <TableHead sx={{ backgroundColor: 'transparent !important' }}>
            {headerGroups.map((headerGroup, index) => (
              <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ backgroundColor: 'transparent !important' }}>
                {headerGroup.headers.map((column: any, idx: number) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    {...column.getHeaderProps([{ className: column.className }, getHeaderProps?.(column)])}
                    sx={{
                      ...tableHeaderStyles,
                      ...(idx === headerGroup.headers.length - 1 && {
                        pr: '24px !important',
                        whiteSpace: 'nowrap',
                        width: lastTHWidth ?? '1%'
                      })
                    }}
                  >
                    <HeaderSort column={{ ...column, disableSortBy: true }} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          {/* Table Body */}
          <TableBody {...getTableBodyProps()}>
            {rows.length > 0 || isLoading ? (
              rows.map((row: any) => {
                prepareRow(row);
                const isExpanded = Array.isArray(expandedRow) ? expandedRow.includes(row.index) : expandedRow === row.index;
                const preview = isExpanded ? null : renderRowPreview?.(row.original);
                // Both hang off the bottom of the row, so both suppress its closing edge.
                const attached = isExpanded || Boolean(preview);

                return (
                  <React.Fragment key={row.id ?? row.original?.id ?? row.index}>
                    <TableRow
                      {...row.getRowProps()}
                      sx={{
                        backgroundColor:
                          childRowHovered && isExpanded
                            ? theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                            : theme.palette.background.default,
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05) !important'
                              : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                        },
                        '&:hover + tr': {
                          backgroundColor:
                            isExpanded &&
                            (theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05) !important'
                              : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`)
                        }
                      }}
                    >
                      {row.cells.map((cell: any, idx: number) => (
                        <TableCell
                          key={cell.column.id || idx}
                          align={cell.column.align}
                          {...cell.getCellProps([{ className: cell.column.className }])}
                          sx={{
                            ...tableCellStyles,
                            ...(attached && {
                              borderBottom: 'none !important'
                            }),
                            ...(idx === 0 && {
                              pl: '16px',
                              borderLeft: '1px solid',
                              borderTopLeftRadius: '8px',
                              borderBottomLeftRadius: attached ? '0' : '8px'
                            }),
                            ...(idx === row.cells.length - 1 && {
                              pr: '24px',
                              borderRight: '1px solid',
                              borderTopRightRadius: '8px',
                              borderBottomRightRadius: attached ? '0' : '8px'
                            })
                          }}
                        >
                          {cell.render('Cell')}
                        </TableCell>
                      ))}
                    </TableRow>
                    {/* Footer row: the collapsed row's preview, or the expanded content in its place. */}
                    {(renderExpandedRow || preview) && (
                      <TableRow
                        sx={{
                          pl: '0px !important',
                          marginTop: '-8px !important',
                          transform: 'translateY(-8px)',
                          backgroundColor: theme.palette.background.default,
                          '&:hover': {
                            backgroundColor: isExpanded
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05) !important'
                                : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                              : undefined
                          }
                        }}
                      >
                        <TableCell
                          colSpan={columns.length}
                          sx={{
                            ...footerCellSx(attached, isExpanded),
                            borderBottomLeftRadius: attached ? '8px' : '0',
                            borderBottomRightRadius: attached ? '8px' : '0',
                            overflow: 'hidden'
                          }}
                        >
                          {preview}
                          <Collapse
                            onMouseEnter={() => setChildRowHovered(true)}
                            onMouseLeave={() => setChildRowHovered(false)}
                            in={isExpanded}
                            timeout={250}
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                width: '100%',
                                p: expandedRowPadding,
                                boxSizing: 'border-box'
                              }}
                            >
                              {renderExpandedRow?.(row.original)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <EmptyTable msg="No Data" colSpan={columns?.length} />
            )}

            {footer && (
              <TableRow
                sx={{
                  backgroundColor: theme.palette.grey[700],
                  '& td': {
                    color: 'white',
                    fontWeight: 600,
                    border: 'none'
                  },
                  ...footerSx
                }}
              >
                {columns.map((col, idx) => (
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      fontSize: textSizeByBreakpoint(14),
                      border: 'none',
                      paddingX: '12px',
                      paddingY: sizeByBreakpoint(1, 12),
                      backgroundColor: theme.palette.grey[700],
                      textAlign: 'left',
                      ...(idx === 0 && {
                        borderTopLeftRadius: '10px',
                        borderBottomLeftRadius: '10px',
                        pl: '16px'
                      }),
                      ...(idx === columns.length - 1 && {
                        borderTopRightRadius: '10px',
                        borderBottomRightRadius: '10px',
                        pr: '24px'
                      }),
                      ...fullFooterCellSx,
                      ...footerCellSxByColumn?.[col.accessor as string]
                    }}
                    key={String(col.accessor ?? col.id)}
                  >
                    {typeof footer === 'object' ? (footer[col.accessor as string] ?? '') : footer}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </GradientFadeBox>

      {/* Bottom Pagination (outside GradientFadeBox so it is always visible) */}
      {shouldShowPagination && (
        <Box sx={{ pt: 2, flexShrink: 0 }}>
          <TablePagination
            handlePageSize={handlePageSize}
            gotoPage={handlePageChange as (value: 'next' | 'prev' | number) => void}
            rows={rows}
            setPageSize={() => {}}
            pageIndex={pageIndex}
            pageSize={pageSize}
            dataCount={dataCount}
            hidePagePerRow={hidePagePerRow}
            cursorBased={cursorBased}
            hasNextPage={nextEnabled}
            onGotoFirst={onGotoFirst}
          />
        </Box>
      )}
    </Stack>
  );
};

export default ReactTable;
